import { useNavigate } from "@solidjs/router";
import { ObtainUserContextRequestConfig, SwitchUserContextRequestConfig, useStorytellClient } from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { FocusedContext, SwitchFocusRequest } from "@storytell/types";
import { batch, createResource, createSignal } from "solid-js";

export type UserContextService = ReturnType<typeof createUserContextService>;

export const createUserContextService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();
  const navigate = useNavigate();

  const [userContextData, { refetch, mutate }] = createResource(
    () => deps.authReady() && deps.userId(),
    async (): Promise<FocusedContext | undefined> => {
      const request = ObtainUserContextRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch user context", response.error);
        return undefined;
      }
      return response.value;
    },
    {
      deferStream: true,
    },
  );

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (userContextData() ? undefined : undefined);

  const get = () => userContextData.latest;

  const switchContext = async (
    input: SwitchFocusRequest,
    dontResetNavigation = false,
  ): Promise<FocusedContext | undefined> => {
    const request = SwitchUserContextRequestConfig.withBearerToken(deps.authToken());
    const response = await client.fetch(request, undefined, input);
    if (response.isErr()) {
      console.error("Failed to switch user context", response.error);
      return undefined;
    }

    batch(() => {
      mutate(response.value);
      dispatchGlobalEvent("user-context-changed", { projectId: response.value.projectId });
      if (!dontResetNavigation) {
        navigate(`/projects/${response.value.projectId}`);
      }
    });

    return response.value;
  };

  return {
    suspenseTrigger,
    refetch,
    get,
    switchContext,
  };
};
